import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Icon } from './Icon.tsx';
import { invitesApi, type Invite } from '../features/invites/api.ts';
import { vendorTypesApi, type VendorType } from '../features/vendorTypes/api.ts';
import { useAuthStore } from '../store/auth.ts';
import { canProcure } from '../lib/roles.ts';
import { CountdownBadge } from './CountdownBadge.tsx';
import { t } from '../lib/strings.ts';
import { formatDate } from '@/lib/format';

interface Props {
  requestId: string;
  deadline: string | null;
}

const DEFAULT_WINDOW_HOURS = 6;

function clampWindow(raw: string): number {
  const n = parseInt(raw, 10);
  if (isNaN(n)) return DEFAULT_WINDOW_HOURS;
  return Math.min(24, Math.max(1, n));
}

function InviteRow({ invite, requestId }: { invite: Invite; requestId: string }) {
  const qc = useQueryClient();

  const resendMutation = useMutation({
    mutationFn: () => invitesApi.resend(requestId, invite.vendorId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['invites', requestId] });
    },
  });

  return (
    <li className="px-4 py-3.5">
      {/* Mobile card */}
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="font-medium leading-snug">{invite.vendorName}</p>
          {invite.vendorTypes.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {invite.vendorTypes.map((vt) => (
                <span key={vt.id} className="pill pill-neutral text-xs">{vt.name}</span>
              ))}
            </div>
          )}
          {invite.deliveryError && invite.status === 'PENDING' && (
            <div className="mt-1.5 flex flex-wrap items-center gap-2">
              <span className="flex items-center gap-1 text-xs text-destructive">
                <Icon name="error" size={14} />
                {t.requestDetail.sendFail}
              </span>
              <button
                type="button"
                onClick={() => resendMutation.mutate()}
                disabled={resendMutation.isPending}
                className="btn-ghost btn-sm gap-1 text-xs"
              >
                <Icon name="refresh" size={14} className={resendMutation.isPending ? 'animate-spin' : ''} />
                {resendMutation.isPending ? t.requestDetail.resending : t.requestDetail.resend}
              </button>
              {resendMutation.isError && (
                <span className="text-xs text-destructive">{t.requestDetail.resendFailed}</span>
              )}
            </div>
          )}
        </div>
        <div className="shrink-0">
          {invite.status === 'SUBMITTED' ? (
            <span className="pill pill-success">{t.requestDetail.statusSubmitted}</span>
          ) : (
            <span className="pill pill-pending">{t.requestDetail.statusPending}</span>
          )}
        </div>
      </div>
    </li>
  );
}

export function InvitesPanel({ requestId, deadline }: Props) {
  const qc = useQueryClient();
  const role = useAuthStore((s) => s.user?.role);
  const canManage = canProcure(role);
  const [selectedTypeIds, setSelectedTypeIds] = useState<Set<string>>(new Set());
  const [windowHoursStr, setWindowHoursStr] = useState(String(DEFAULT_WINDOW_HOURS));
  const [extendHoursStr, setExtendHoursStr] = useState(String(DEFAULT_WINDOW_HOURS));
  const [extendMsg, setExtendMsg] = useState<{ kind: 'ok' | 'error' | 'enough'; } | null>(null);
  const [sendResult, setSendResult] = useState<{
    sent: number;
    failed: { vendorName: string; vendorId: string; error: string }[];
    skipped: { vendorName: string; reason: 'no_email' }[];
    alreadyInvited: number;
  } | null>(null);

  const { data: invites = [], isLoading: invitesLoading } = useQuery({
    queryKey: ['invites', requestId],
    queryFn: () => invitesApi.list(requestId),
    refetchInterval: (query) => {
      const data = query.state.data as Invite[] | undefined;
      return data?.some((i) => i.status === 'PENDING') ? 10000 : false;
    },
  });

  const { data: types = [] } = useQuery({
    queryKey: ['vendor-types'],
    queryFn: () => vendorTypesApi.list(),
  });

  const sendMutation = useMutation({
    mutationFn: (args: { typeIds: string[]; windowHours: number }) =>
      invitesApi.sendForQuotes(requestId, args.typeIds, args.windowHours),
    onSuccess: (result) => {
      setSendResult(result);
      setSelectedTypeIds(new Set());
      void qc.invalidateQueries({ queryKey: ['invites', requestId] });
      void qc.invalidateQueries({ queryKey: ['request', requestId] });
    },
  });

  const extendMutation = useMutation({
    mutationFn: (windowHours: number) => invitesApi.extendDeadline(requestId, windowHours),
    onSuccess: () => {
      setExtendMsg({ kind: 'ok' });
      void qc.invalidateQueries({ queryKey: ['request', requestId] });
    },
    onError: (err: unknown) => {
      const code = (err as { response?: { status?: number; data?: { error?: string } } }).response;
      if (code?.status === 409 && code.data?.error === 'enough_quotes') {
        setExtendMsg({ kind: 'enough' });
      } else {
        setExtendMsg({ kind: 'error' });
      }
    },
  });

  function toggleType(id: string) {
    setSelectedTypeIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function handleSend() {
    if (selectedTypeIds.size === 0) return;
    setSendResult(null);
    sendMutation.mutate({ typeIds: [...selectedTypeIds], windowHours: clampWindow(windowHoursStr) });
  }

  function handleExtend() {
    setExtendMsg(null);
    extendMutation.mutate(clampWindow(extendHoursStr));
  }

  return (
    <div className="space-y-4">
      <div className="section-heading">
        <h2 className="status-label text-muted-foreground">{t.requestDetail.vendorInvitations}</h2>
      </div>

      {/* Type-group selector — procurement roles only */}
      {canManage && (
      <div className="card-paper p-4 space-y-3">
        <p className="text-sm font-medium">{t.requestDetail.selectVendorTypes}</p>

        {types.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t.requestDetail.noVendorTypes}
          </p>
        ) : (
          <div className="space-y-2">
            {types.map((vt: VendorType) => (
              <label key={vt.id} className="flex cursor-pointer items-center gap-2 text-sm select-none">
                <input
                  type="checkbox"
                  checked={selectedTypeIds.has(vt.id)}
                  onChange={() => toggleType(vt.id)}
                  className="h-4 w-4 rounded border-border accent-primary"
                />
                <span>{vt.name}</span>
              </label>
            ))}
          </div>
        )}

        {/* Submission window */}
        <div className="space-y-1 pt-1">
          <label htmlFor="window-hours" className="field-label text-xs">{t.requestDetail.windowHoursLabel}</label>
          <div className="flex items-center gap-2">
            <input
              id="window-hours"
              type="number"
              min={1}
              max={24}
              className="input num w-24"
              value={windowHoursStr}
              onChange={(e) => setWindowHoursStr(e.target.value)}
              dir="ltr"
            />
            <span className="text-xs text-muted-foreground">{t.requestDetail.windowHoursHint}</span>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 pt-1">
          <button
            onClick={handleSend}
            disabled={selectedTypeIds.size === 0 || sendMutation.isPending}
            className="btn-primary"
          >
            {sendMutation.isPending ? t.requestDetail.sending : t.requestDetail.sendForQuotes}
          </button>
          {sendMutation.isError && (
            <p className="text-sm text-destructive">{t.requestDetail.sendFailed}</p>
          )}
        </div>

        {/* Send result summary */}
        {sendResult && (
          <div className="rounded-md border border-border bg-muted/40 p-3 text-sm space-y-2">
            <p className="font-medium text-ink">
              {t.requestDetail.sentTo} <span className="num">{sendResult.sent}</span> {sendResult.sent !== 1 ? t.requestDetail.vendors : t.requestDetail.vendor}
              {sendResult.alreadyInvited > 0 && (
                <span className="ms-1 font-normal text-muted-foreground">
                  (<span className="num">{sendResult.alreadyInvited}</span> {t.requestDetail.alreadyInvited})
                </span>
              )}
            </p>
            {sendResult.skipped.length > 0 && (
              <div>
                <p className="text-muted-foreground">{t.requestDetail.skippedNoEmail}</p>
                <ul className="mt-1 list-disc ps-4 text-muted-foreground">
                  {sendResult.skipped.map((s, i) => (
                    <li key={i}>{s.vendorName}</li>
                  ))}
                </ul>
              </div>
            )}
            {sendResult.failed.length > 0 && (
              <div>
                <p className="text-destructive">{t.requestDetail.failedDeliver}</p>
                <ul className="mt-1 list-disc ps-4 text-destructive">
                  {sendResult.failed.map((f, i) => (
                    <li key={i}>{f.vendorName}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
      )}

      {/* Extend submission window — procurement roles only */}
      {canManage && deadline && (
        <div className="card-paper p-4 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-medium">
              {t.requestDetail.deadlineLabel}: <span className="num">{formatDate(deadline)}</span>
            </p>
            <CountdownBadge deadline={deadline} />
          </div>
          <div className="space-y-1">
            <label htmlFor="extend-hours" className="field-label text-xs">{t.requestDetail.windowHoursLabel}</label>
            <div className="flex flex-wrap items-center gap-2">
              <input
                id="extend-hours"
                type="number"
                min={1}
                max={24}
                className="input num w-24"
                value={extendHoursStr}
                onChange={(e) => setExtendHoursStr(e.target.value)}
                dir="ltr"
              />
              <span className="text-xs text-muted-foreground">{t.requestDetail.windowHoursHint}</span>
              <button
                onClick={handleExtend}
                disabled={extendMutation.isPending}
                className="btn-secondary btn-sm gap-1.5"
              >
                <Icon name="schedule" size={14} />
                {extendMutation.isPending ? t.requestDetail.extending : t.requestDetail.extendWindow}
              </button>
            </div>
          </div>
          {extendMsg?.kind === 'ok' && <p className="text-sm text-success">{t.requestDetail.extendOk}</p>}
          {extendMsg?.kind === 'enough' && <p className="text-sm text-muted-foreground">{t.requestDetail.extendEnoughQuotes}</p>}
          {extendMsg?.kind === 'error' && <p className="text-sm text-destructive">{t.requestDetail.extendFailed}</p>}
        </div>
      )}

      {/* Invite list */}
      {invitesLoading ? (
        <p className="text-sm text-muted-foreground">{t.requestDetail.inviteLoading}</p>
      ) : invites.length > 0 ? (
        <ul className="rounded-3xl bg-[hsl(var(--chrome))] divide-y divide-black/[0.05] overflow-hidden">
          {invites.map((invite) => (
            <InviteRow key={invite.id} invite={invite} requestId={requestId} />
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">{t.requestDetail.noInvites}</p>
      )}
    </div>
  );
}
